/* eslint-disable react/no-danger */
import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { ReactComponent as FirstCaptureInstruction } from 'assets/svgs/manual-labelling/instr-first-capture.svg';
import { ReactComponent as SecondCaptureInstruction } from 'assets/svgs/manual-labelling/instr-second-capture.svg';
import { ReactComponent as ChipCaptureInstruction } from 'assets/svgs/manual-labelling/instr-chip-capture.svg';
import { ReactComponent as Bulb } from 'assets/svgs/icn-bulb.svg';
import { Button, SkeletonUi } from 'components/partials';
// Imported directly (not via the partials barrel): styled(Thumbnail) runs at module
// evaluation, and the barrel is circular through private-route -> components/pages
import { Thumbnail } from 'components/partials/thumbnail/thumbnail.component';
import { useIsScreenPortrait, useTheme } from 'hooks';
import { useTranslate } from 'services';
import { IDamage, DamageType, ICustomDamageType, BodyPart } from 'redux/damages/damages';
import { TRANSLATIONS_VALUES_KEYS } from 'redux/internationalization/internationalization';
import { SubTypeConfiguration, ADDITIONAL_SUB_TYPES } from 'redux/workflows/workflows';
import { PhotoSeries } from 'redux/photoseries/photoseries';
import { getPhotoSeries } from 'redux/root.selectors';
import { SvgWrapper } from 'assets';

const {
  damage_labelling: {
    damage_type,
    capture_damage_title,
    damage_type_text,
    capture_damage_description,
    body_part,
    body_part_text,
    capture_chip_damage_text: capture_chip_default_txt
  },
  damage_labelling_crack_repair: {
    capture_chip_damage_text: capture_chip_crack_repair_txt
  },
  instructions: {
    skip
  }
} = TRANSLATIONS_VALUES_KEYS;

// Height is not inherited from parent and I have no idea why.
// That is the reason I am using min-heigh: 76vh, instead of 100%
const Wrapper = styled.div<{isPortrait: boolean}>`
  display: flex;
  flex-direction: column;
  min-height: ${({ isPortrait }) => isPortrait ? '76vw' : '76vh'};
`;

const Row = styled.div<{ flex?: number }>`
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  ${({ flex }) => flex ? `flex: ${flex};` : ''}
`;

const InformationWrapper = styled.div`
  padding-top: 3.188rem;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
`;

const Column = styled.div<{ flex: number }>`
  flex: ${(props) => `${props.flex}%`};
`;

const Title = styled.div`
  font-size: 1.563rem;
  font-weight: bold;
`;

const ThumbnailWrapper = styled.div`
  height: 100%
  display: flex;
  justify-content: flex-end;
  align-items: flex-end;
  position: relative;
`;

const ThumbnailImage = styled(Thumbnail)`
  border-radius: 10%;
  width: 13.125rem;
`;

const DamageInformation = styled.div`
  display: flex;
  flex-direction: column;
  font-size: 0.75rem;
`;

const FirstCapture = styled(FirstCaptureInstruction)`
  width: 6.5rem;
  position: absolute;
  right: 8.5rem;
  bottom: -1rem;
`;

const SecondCapture = styled(SecondCaptureInstruction)`
  width: 6rem;
  position: absolute;
  right: -2rem;
  bottom: -1rem;
`;

const ChipCapture = styled(ChipCaptureInstruction)`
  width: 10rem;
  position: absolute;
  right: -2.5rem;
  bottom: -3rem;
`;

interface MarkedDamagesPropTypes {
  damage: IDamage,
  imageSubTypes: SubTypeConfiguration[],
  customDamageTypes: ICustomDamageType[],
  onSkip: () => void
}

const MarkedDamages: React.FC<MarkedDamagesPropTypes> = ({ customDamageTypes, damage, imageSubTypes, onSkip }) => {
  const { styles } = useTheme();
  const { id, imageFileName } = damage;

  const [imageLoaded, setImageLoaded] = useState(false);

  const isPortrait = useIsScreenPortrait();
  const firstImageCapture = damage.additionalImagesTaken === undefined ||
                            damage.additionalImagesTaken !== 1;
  const { translate, translateUnsafe } = useTranslate();

  const subTypeConfiguration = firstImageCapture
    ? imageSubTypes.find((cfg) => cfg.imageSubType === ADDITIONAL_SUB_TYPES.FIRST_CLOSE_UP)
    : imageSubTypes.find((cfg) => cfg.imageSubType === ADDITIONAL_SUB_TYPES.SECOND_CLOSE_UP);

  const hasSecondCloseUp = imageSubTypes.some((cfg) => cfg.imageSubType === ADDITIONAL_SUB_TYPES.SECOND_CLOSE_UP);
  const isSmallDamageCapture = damage.damageType === DamageType.Chip || (damage.damageType === DamageType.Crack && damage.isSmall);

  const photoSeries: PhotoSeries = useSelector(getPhotoSeries);

  const capture_chip_damage_text = photoSeries.manualDamageLabellingCrackRepairEnabled
    ? capture_chip_crack_repair_txt
    : capture_chip_default_txt;

  useEffect(() => {
    setImageLoaded(false);
  }, [imageFileName, id]);

  let damageType;

  if (!damage.customDamageTypeId) {
    Object.keys(DamageType).forEach((key: string) => {
      if (DamageType[key as keyof typeof DamageType] === damage.damageType) {
        damageType = translate(damage_type[key.toLowerCase() as keyof typeof damage_type]);
      }
    });
  } else {
    damageType = customDamageTypes.find((c) => c.id === damage.customDamageTypeId)?.name;
  }

  const hideDamageType = photoSeries.manualLabellingWindshieldDamageDetection;

  return (
    <Wrapper isPortrait={isPortrait}>
      <Row>
        <Title data-testid="title">{translate(capture_damage_title)}</Title>
      </Row>
      <Row flex={1}>
        <Column flex={40}>
          <InformationWrapper>
            {
              !isSmallDamageCapture && (
                <div>
                  { translateUnsafe(capture_damage_description) }
                </div>
              )
            }
            {
              isSmallDamageCapture && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0.6rem',
                  backgroundColor: styles.colorBackgroundRaised,
                  borderRadius: '0.5rem',
                }}
                >
                  <SvgWrapper
                    size="small"
                    fill={styles.colorBackgroundRaised}
                    stroke={styles.colorParagraph}
                  >
                    <Bulb />
                  </SvgWrapper>
                  <span
                    style={{ fontSize: '0.75rem', marginLeft: '0.5rem', fontWeight: 600 }}
                  >
                    {translate(capture_chip_damage_text)}
                  </span>
                </div>
              )
            }
            <div>
              <DamageInformation>
                {
                  !hideDamageType && (
                    <span>{translate(damage_type_text)}: <b>{damageType}</b></span>
                  )
                }
                <span>
                  {translate(body_part_text)}: <b>{translate(body_part[BodyPart[damage.bodyPart].toLowerCase() as keyof typeof body_part])}</b>
                </span>
              </DamageInformation>
              {
                subTypeConfiguration?.optional && (
                  <Button
                    style={{ marginTop: '.75rem' }}
                    type="button"
                    testId="skip"
                    bg={styles.colorSecondaryButtonBackground}
                    color={styles.colorSecondaryButtonText}
                    hoverBorderColor={styles.colorSecondaryButtonBackgroundDark}
                    hoverShadowColor={styles.colorSecondaryButtonBackgroundLight}
                    onClick={onSkip}
                  >
                    {translate(skip)}
                  </Button>
                )
              }
            </div>
          </InformationWrapper>
        </Column>
        <Column flex={60}>
          <ThumbnailWrapper>
            { !imageLoaded && <SkeletonUi width="13.125rem" height="13.125rem" shape="radiusHorizone" noMargin /> }
            {
              imageFileName && (
                <ThumbnailImage
                  f={imageFileName}
                  w="small"
                  d={id}
                  alt="Damage thumbnail"
                  retryPolicy={{ count: 3, delayMs: 5000 }}
                  onLoad={() => setImageLoaded(true)}
                  style={imageLoaded ? undefined : { display: 'none' }}
                />
              )
            }
            { !isSmallDamageCapture && firstImageCapture && <FirstCapture /> }
            { !isSmallDamageCapture && hasSecondCloseUp && !firstImageCapture && <SecondCapture /> }
            { isSmallDamageCapture && <ChipCapture /> }
          </ThumbnailWrapper>
        </Column>
      </Row>
    </Wrapper>
  );
};

export default MarkedDamages;
